/**
 * Microphone recording, normalised to a format the platform can play.
 *
 * The SpeedRadio app plays aac, m4a, mp3 and wav — and defaults to aac.
 * Browsers record what they like: Safari gives MP4/AAC, Chrome usually gives
 * WebM/Opus, which the app cannot play at all. A WebM upload would pass
 * review and only fail once published, which is the worst possible moment to
 * find out, so the take is converted here. See lib/audio.ts for the three
 * routes and why AAC is preferred.
 *
 * Mono at 64 kbps: voice notes gain nothing from stereo, and the smaller file
 * encodes and uploads far faster on a phone.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { pickRecordingMimeType, prepareForUpload } from "../lib/audio";

export type RecorderState = "idle" | "requesting" | "recording" | "encoding" | "ready" | "error";

export interface RecordedClip {
  blob: Blob;
  url: string;
  durationSeconds: number;
  /** What to upload it as — the API validates both. */
  contentType: string;
  extension: string;
  /** Which encoding route produced it, surfaced for support. */
  route: "native-aac" | "webcodecs-aac" | "mp3";
}

interface UseRecorder {
  state: RecorderState;
  error: string | null;
  seconds: number;
  paused: boolean;
  /** 0..1 per bar, for the live waveform. */
  levels: number[];
  clip: RecordedClip | null;
  start: () => Promise<void>;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
}

const DEFAULT_BAR_COUNT = 28;
const IDLE_LEVEL = 0.08;
/** How often a new bar enters the live waveform. */
const SAMPLE_MS = 70;

/** The whole take squeezed into `bars` columns, peak per column. */
export function summariseLevels(history: number[], bars: number): number[] {
  if (history.length === 0) return new Array(bars).fill(IDLE_LEVEL);
  const out: number[] = [];
  for (let i = 0; i < bars; i += 1) {
    const from = Math.floor((i * history.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * history.length) / bars));
    let peak = 0;
    for (let j = from; j < to && j < history.length; j += 1) peak = Math.max(peak, history[j]);
    out.push(Math.max(IDLE_LEVEL, peak));
  }
  return out;
}

/**
 * How far before the limit the recorder stops itself.
 *
 * The tick runs every 100ms and the recorder flushes the slice it is part
 * way through, so stopping exactly on the limit yields audio a fraction over
 * it — which the API then refuses. A quarter of a second covers both, and is
 * short enough that nobody notices it missing.
 */
export const STOP_MARGIN_SECONDS = 0.25;

export function shouldStop(elapsedSeconds: number, maxSeconds: number): boolean {
  return elapsedSeconds >= Math.max(0, maxSeconds - STOP_MARGIN_SECONDS);
}

export function useRecorder(maxSeconds = 180, barCount = DEFAULT_BAR_COUNT): UseRecorder {
  const BAR_COUNT = barCount;
  const [state, setState] = useState<RecorderState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [paused, setPaused] = useState(false);
  const [levels, setLevels] = useState<number[]>(() => new Array(BAR_COUNT).fill(IDLE_LEVEL));
  const historyRef = useRef<number[]>([]);
  const pausedRef = useRef(false);
  const [clip, setClip] = useState<RecordedClip | null>(null);
  const pausedAccumRef = useRef(0);
  const pauseStartedRef = useRef<number | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const clipUrlRef = useRef<string | null>(null);
  // Read inside the interval callback, which closes over the value it was
  // created with. Synced in an effect rather than during render.
  const maxRef = useRef(maxSeconds);
  useEffect(() => {
    maxRef.current = maxSeconds;
  }, [maxSeconds]);

  const teardown = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) window.clearInterval(timerRef.current);
    rafRef.current = null;
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      void audioCtxRef.current.close();
    }
    audioCtxRef.current = null;
  }, []);

  // Stop the microphone if the component goes away mid-recording — a live
  // mic indicator left on after navigating away is alarming, and rightly so.
  useEffect(() => {
    return () => {
      teardown();
      if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    };
  }, [teardown]);

  const reset = useCallback(() => {
    teardown();
    if (clipUrlRef.current) {
      URL.revokeObjectURL(clipUrlRef.current);
      clipUrlRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setClip(null);
    setSeconds(0);
    setPaused(false);
    pausedRef.current = false;
    pausedAccumRef.current = 0;
    pauseStartedRef.current = null;
    historyRef.current = [];
    setLevels(new Array(BAR_COUNT).fill(IDLE_LEVEL));
    setError(null);
    setState("idle");
  }, [teardown, BAR_COUNT]);

  const finish = useCallback(async () => {
    const recorded = new Blob(chunksRef.current, {
      type: recorderRef.current?.mimeType || "audio/webm",
    });
    const elapsed = (Date.now() - startedAtRef.current) / 1000;
    teardown();
    setLevels(summariseLevels(historyRef.current, BAR_COUNT));

    if (recorded.size === 0) {
      setError("Nothing was recorded. Check your microphone and try again.");
      setState("error");
      return;
    }

    setState("encoding");
    try {
      const prepared = await prepareForUpload(recorded, maxRef.current);
      const url = URL.createObjectURL(prepared.blob);
      clipUrlRef.current = url;
      setClip({
        blob: prepared.blob,
        url,
        // Duration from the decoded audio is exact; the wall-clock elapsed
        // time is only a fallback for when decoding could not measure it.
        durationSeconds: prepared.durationSeconds || elapsed,
        contentType: prepared.contentType,
        extension: prepared.extension,
        route: prepared.route,
      });
      setState("ready");
    } catch (err) {
      console.error("Could not prepare the recording for upload", err);
      setError("Couldn't process that recording. Please try again.");
      setState("error");
    }
  }, [teardown, BAR_COUNT]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && (recorder.state === "recording" || recorder.state === "paused")) {
      recorder.stop();
    }
  }, []);

  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "recording" || typeof recorder.pause !== "function") return;
    recorder.pause();
    pauseStartedRef.current = Date.now();
    pausedRef.current = true;
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== "paused" || typeof recorder.resume !== "function") return;
    if (pauseStartedRef.current) {
      pausedAccumRef.current += Date.now() - pauseStartedRef.current;
      pauseStartedRef.current = null;
    }
    recorder.resume();
    pausedRef.current = false;
    setPaused(false);
  }, []);

  const start = useCallback(async () => {
    reset();
    setState("requesting");

    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This browser can't record audio. Try Chrome, Safari or Firefox.");
      setState("error");
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      const name = (err as Error).name;
      setError(
        name === "NotAllowedError"
          ? "Microphone access was blocked. Allow it in your browser settings, then try again."
          : name === "NotFoundError"
            ? "No microphone found. Plug one in or check your device settings."
            : "Couldn't start the microphone. Try again.",
      );
      setState("error");
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    // Prefer a container that is already AAC, so there is nothing to convert.
    const mimeType = pickRecordingMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      void finish();
    };

    // Live level meter for the waveform.
    const AudioCtx = window.AudioContext || (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let peak = 0;
    let smoothed = IDLE_LEVEL;
    let lastPush = 0;

    // Loudness rather than raw samples: RMS per frame, the loudest frame in
    // each slice, then a fast rise and slower fall so speech reads as speech
    // and not as flicker.
    const tick = (now: number) => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const value = (data[i] - 128) / 128;
        sum += value * value;
      }
      peak = Math.max(peak, Math.sqrt(sum / data.length));
      if (now - lastPush >= SAMPLE_MS) {
        lastPush = now;
        if (!pausedRef.current) {
          const target = Math.min(1, Math.pow(peak * 4.2, 0.75));
          smoothed = target > smoothed ? target : smoothed * 0.6 + target * 0.4;
          const level = Math.max(IDLE_LEVEL, smoothed);
          historyRef.current.push(level);
          setLevels((previous) => [...previous.slice(1), level]);
        }
        peak = 0;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    startedAtRef.current = Date.now();
    recorder.start(250);
    setState("recording");
    rafRef.current = requestAnimationFrame(tick);

    timerRef.current = window.setInterval(() => {
      const held = pauseStartedRef.current
        ? Date.now() - pauseStartedRef.current
        : 0;
      const elapsed = (Date.now() - startedAtRef.current - pausedAccumRef.current - held) / 1000;
      setSeconds(Math.max(0, elapsed));
      // Hard stop at the limit rather than letting someone record five
      // minutes and only then be told it is too long — a shade early, since
      // the recorder keeps whatever slice is in flight and this only ticks
      // ten times a second. See STOP_MARGIN_SECONDS.
      if (!pauseStartedRef.current && shouldStop(elapsed, maxRef.current)) stop();
    }, 100);
  }, [finish, reset, stop]);

  return { state, error, seconds, paused, levels, clip, start, stop, pause, resume, reset };
}
